// src/components/home/ServicesSection.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

// Service icons & images
import icon1 from '../../assets/A-icon5.png';
import icon2 from '../../assets/A-icon4.png';
import icon3 from '../../assets/A-icon6.png';
import icon4 from '../../assets/A-icon1.png';
import icon5 from '../../assets/A-icon3.png';
import icon6 from '../../assets/A-icon2.png';

import therapyImg from '../../assets/Herbal Remedies.png';
import ShirodharaTreatment from '../../assets/Shirodhara Treatment.png';
import PanchakarmaTherapy from '../../assets/A-image6.png';
import DetoxRejuvenation from '../../assets/Detox.png';
import AbhyangaMassage from '../../assets/AbhyangaMassage3.png';
import HolisticHealing from '../../assets/HolisticHealing.png';
import stemImage from '../../assets/stemImage4.png';

import cardBg1 from '../../assets/A-card-bg1.png';

const ServicesSection = () => {
  const services = [
    {
      id: 1,
      icon: icon1,
      title: "Herbal Remedies",
      description: "Natural herbs and oils carefully selected\nfor your unique Dosha type.\nSupports body’s natural healing process\nand helps maintain long-term balance.",
      image: therapyImg,
      path: "/services/herbal-remedies"
    },
    {
      id: 2,
      icon: icon2,
      title: "Shirodhara Treatment",
      description: "Warm medicated oil gently streamed onto the forehead.\nRelaxes the mind and calms the nervous system.\nReduces stress and mental fatigue,\npromoting deep relaxation and clarity.",
      image: ShirodharaTreatment,
      path: "/services/shirodhara"
    },
    {
      id: 3,
      icon: icon3,
      title: "Panchakarma Therapy",
      description: "A complete detoxification program to cleanse the body.\nRemoves toxins and purifies vital organs.\nRestores energy and rejuvenates mind and body,\nenhancing overall vitality and well-being.",
      image: PanchakarmaTherapy,
      path: "/services/panchakarma"
    },
    {
      id: 4,
      icon: icon4,
      title: "Detox & Rejuvenation",
      description: "Holistic programs designed to purify and rejuvenate the body.\nBoosts energy levels and strengthens immunity.\nPromotes mental clarity and physical vitality,\nhelping you feel refreshed and balanced.",
      image: DetoxRejuvenation,
      path: "/services/detox"
    },
    {
      id: 5,
      icon: icon5,
      title: "Abhyanga Massage",
      description: "A warm oil massage that nourishes muscles and tissues.\nImproves blood circulation and eases tension.\nSupports lymphatic detoxification and flexibility,\nleaving the body relaxed and revitalized.",
      image: AbhyangaMassage,
      path: "/services/abhyanga"
    },
    {
      id: 6,
      icon: icon6,
      title: "Holistic Healing",
      description: "A mind-body balancing approach for complete well-being.\nReduces stress and promotes better digestion.\nEnhances inner peace, mental clarity, and emotional stability,\nhelping you achieve overall harmony.",
      image: HolisticHealing,
      path: "/services/holistic-healing"
    }
  ];

  const [activeServiceIndex, setActiveServiceIndex] = useState(0);
  const [hoveredService, setHoveredService] = useState(null);
  const [wheelAngle, setWheelAngle] = useState(0);
  const [wheelPaused, setWheelPaused] = useState(false);
  const [radius, setRadius] = useState(260);

  const wheelRef = useRef(null);
  const iconRefs = useRef([]);
  const animationRef = useRef(0);
  const wheelPausedRef = useRef(false);
  const autoRotationIntervalRef = useRef(null);
  const navigate = useNavigate();

  // Responsive radius
  useEffect(() => {
    const updateRadius = () => {
      if (window.innerWidth < 480) setRadius(140);
      else if (window.innerWidth < 768) setRadius(180);
      else if (window.innerWidth < 1024) setRadius(220);
      else setRadius(260);
    };
    updateRadius();
    window.addEventListener('resize', updateRadius);
    return () => window.removeEventListener('resize', updateRadius);
  }, []);

  // Sync paused ref
  useEffect(() => {
    wheelPausedRef.current = wheelPaused;
  }, [wheelPaused]);

  // Continuous wheel rotation
  useEffect(() => {
    const animate = () => {
      if (!wheelPausedRef.current) {
        setWheelAngle(prev => (prev + 0.2) % 360);
      }
      animationRef.current = requestAnimationFrame(animate);
    };
    animationRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationRef.current);
  }, []);

  // Auto-select next service every 3s when not paused
  useEffect(() => {
    if (wheelPaused) {
      if (autoRotationIntervalRef.current) clearInterval(autoRotationIntervalRef.current);
      return;
    }
    autoRotationIntervalRef.current = setInterval(() => {
      setActiveServiceIndex(prev => (prev + 1) % services.length);
    }, 3000);
    return () => clearInterval(autoRotationIntervalRef.current);
  }, [wheelPaused, services.length]);

  // Popup positioning logic
  const popupWidth = 224;
  const popupHeight = 160;

  useEffect(() => {
    if (hoveredService === null) return;

    const index = hoveredService;
    const iconEl = iconRefs.current[index];
    if (!iconEl || !wheelRef.current) return;

    const iconRect = iconEl.getBoundingClientRect();
    const wheelRect = wheelRef.current.getBoundingClientRect();

    const relLeft = iconRect.left - wheelRect.left + iconRect.width / 2;
    const relTop = iconRect.top - wheelRect.top + iconRect.height / 2;

    const baseAngle = (index * 360) / services.length;
    const currentAngle = (baseAngle + wheelAngle) % 360;

    const isRight = currentAngle > 300 || currentAngle <= 60;
    const isLeft = currentAngle > 120 && currentAngle <= 240;
    const isTop = currentAngle > 60 && currentAngle <= 120;
    const isBottom = currentAngle > 240 && currentAngle <= 300;

    let popupLeft, popupTop, arrowSide = 'top';
    const sideMargin = 30;
    const verticalMargin = 40;

    if (isBottom) {
      popupLeft = relLeft - popupWidth / 2;
      popupTop = iconRect.top - wheelRect.top - popupHeight - verticalMargin;
      arrowSide = 'bottom';
    } else if (isTop) {
      popupLeft = relLeft - popupWidth / 2;
      popupTop = iconRect.bottom - wheelRect.top + verticalMargin;
      arrowSide = 'top';
    } else if (isRight) {
      popupLeft = iconRect.right - wheelRect.left + sideMargin;
      popupTop = relTop - popupHeight / 2;
      arrowSide = 'left';
    } else {
      popupLeft = iconRect.left - wheelRect.left - popupWidth - sideMargin;
      popupTop = relTop - popupHeight / 2;
      arrowSide = 'right';
    }

    // Store popup position if needed (you can use state here)
    // For now, we use inline styles in JSX
  }, [hoveredService, wheelAngle]);

  const handleServiceClick = (id) => {
    const index = services.findIndex(s => s.id === id);
    setActiveServiceIndex(index);
  };

  // Updated handleViewMore to actually navigate
  const handleViewMore = (serviceId) => {
    const service = services.find(s => s.id === serviceId);
    if (service && service.path) {
      navigate(service.path); // ← This does the real navigation!
    }
  };

  return (
    
    
    <section 
    id="services" 
    className="bg-[#EBE5DB] py-20 sm:py-32 relative overflow-hidden font-playfair"
  >
    <img
  src={stemImage}
  alt="Decorative stem"
  className="absolute top-0 left-0 w-16 sm:w-20 md:w-72 animate-float"
/>
  {/* Top Badge */}
<div class="text-center">
  <div className="services-badge inline-block border border-[#0B2A2C] text-[#0B2A2C] px-4 sm:px-6 py-2 rounded-full text-xl mb-4 sm:mb-6">
     Our Services
  </div>
</div>

  
  {/* Title & Subtitle */}
  <h2 className="services-title text-3xl sm:text-4xl md:text-[2.8rem] text-black-900 font-bold mb-3 sm:mb-4 text-center">
    Healing Through Ayurveda
  </h2>
  <p className="services-subtitle text-base sm:text-lg text-black max-w-xl mx-auto mb-16 sm:mb-20 leading-[1.6] px-2 text-center">
    Discover the power of natural therapies that restore balance and promote
    complete wellness through Ayurveda.
  </p>

  {/* Services Content - Fixed responsive layout */}
<div className="services-content flex flex-col xl2:flex-row items-center justify-center gap-8 sm:gap-12  max-w-7xl mx-auto px-6">
    
{/* Left: Wheel - Now beautiful on mobile */}
<div className="services-wheel-container w-full max-w-md mx-auto relative items-center justify-center left-[-10%] md:left-20 xl2:left-0 xl2:max-w-xl2 xl2:mx-0">
      <div className="logo-wheel" ref={wheelRef} style={{ overflow: 'visible' }}>
        {/* Wheel Container - EXACTLY AS BEFORE */}
        <div className="wheel relative w-full aspect-square max-w-[400px] mx-auto">
          {/* Services Icons in Circle - EXACT ROTATION LOGIC */}
          {services.map((service, index) => {
  const baseAngle = (index * 360) / services.length;
  const currentAngle = (baseAngle + wheelAngle) % 360;
  const isActive = index === activeServiceIndex;

  return (
    <div 
      key={service.id}
      ref={(el) => (iconRefs.current[index] = el)}
      className="wheel-item absolute left-1/2 top-1/2 cursor-pointer group"
      style={{
        transform: `rotate(${currentAngle}deg) translate(${radius}px) rotate(-${currentAngle}deg)`
      }}
      onMouseEnter={() => {
        setActiveServiceIndex(index);
        setWheelPaused(true);
      }}
      onMouseLeave={() => setWheelPaused(false)}
      onClick={() => handleServiceClick(service.id)}
    >
      <div className={`relative ${isActive ? 'z-40' : 'z-10'}`}>
        {/* Active glow ring */}
        {/* Active glow ring - hidden on mobile */}
{isActive && (
  <div className="hidden sm:block absolute inset-0 rounded-full bg-[#0B2A2C]/20 scale-150 animate-ping"></div>
)}


        <div className={`
          upright bg-[#EBE0CF] rounded-full p-4 sm:p-5 shadow-lg 
          border-4 transition-all duration-500
          ${isActive 
            ? 'border-[#0B2A2C] scale-125 shadow-2xl' 
            : 'border-[#0B2A2C]/20 group-hover:border-[#0B2A2C] group-hover:scale-110'
          }
        `}>
          <img 
            src={service.icon} 
            alt={service.title}
            className="w-8 h-8 sm:w-12 sm:h-12 md:w-16 md:h-16 object-contain"
          />
        </div>
      </div>
    </div>
  );
})}
        </div>

        {/* Large Center Image - EXACTLY AS BEFORE */}
        {/* FIXED: Center Image – NO LONGER BLOCKS TOP ICONS */}
              <div 
  className="wheel-center absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-20 pointer-events-none"
  onMouseEnter={() => setWheelPaused(true)}
  onMouseLeave={() => setWheelPaused(false)}
>
  <div className="center-image text-center pointer-events-auto">
    <img 
      src={services[activeServiceIndex].image} 
      alt={services[activeServiceIndex].title}
      className="w-[120px] sm:w-[160px] md:w-[200px] lg:w-[300px] mx-auto h-auto"
    />
    <h3 className="text-lg sm:text-xl md:text-3xl font-bold text-[#0B2A2C] mt-4 sm:mt-6 leading-tight">
      {services[activeServiceIndex].title}
    </h3>
  </div>
</div>



      </div>
    </div>

    

{/* Right: Service Details — MAXIMUM right alignment on desktop */}

<div
  className="services-details 
    max-w-none 
    lg:w-auto lg:max-w-lg xl:max-w-xl 
    
    /* Mobile: centered | Desktop: MAXIMUM right push */
    mx-4 sm:mx-auto 
    lg:ml-auto lg:mr-8 xl:mr-12 2xl:mr-10   
    
    rounded-3xl 
    p-10 sm:p-12 lg:p-10 xl:p-12 
    flex flex-col justify-center items-center 
    min-h-[580px] sm:min-h-[620px] lg:min-h-[640px] 
    mb-16 lg:mb-0 
    -mt-12 lg:mt-0 
    text-center 
    shadow-2xl 
    bg-cover bg-center 
    transition-all duration-500"
  style={{ backgroundImage: `url(${cardBg1})` }}
> 
        
        <div 
        className="service-content transition-all duration-500 ease-in-out"
        onMouseEnter={() => setWheelPaused(true)}
        onMouseLeave={() => setWheelPaused(false)}
      >
        {/* Service Title */}
        <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#0B2A2C] mb-4 sm:mb-6 leading-tight">
          {services[activeServiceIndex].title}
        </h3>

        {/* Service Description */}
        <p className="text-base sm:text-lg text-gray-700 mb-6 sm:mb-8 leading-relaxed">
          {services[activeServiceIndex].description}
        </p>

        {/* Key Features */}
        <div className="service-features mb-6 sm:mb-8 mx-auto w-fit">
  <div className="flex items-center gap-3 text-sm sm:text-base text-[#0B2A2C] mb-3">
    <span className="w-2 h-2 bg-[#0B2A2C] rounded-full "></span>
    <span>Natural & Holistic Approach</span>
  </div>
  <div className="flex items-center gap-3 text-sm sm:text-base text-[#0B2A2C] mb-3">
    <span className="w-2 h-2 bg-[#0B2A2C] rounded-full"></span>
    <span>Personalized Treatment </span>
  </div>
  <div className="flex items-center gap-3 text-sm sm:text-base text-[#0B2A2C]">
    <span className="w-2 h-2 bg-[#0B2A2C] rounded-full"></span>
    <span>Expert Practitioners</span>
  </div>


</div>
        {/* View Details Button */}
        <button 
        onClick={() => handleViewMore(services[activeServiceIndex].id)}
        className="solox-btn view-details-btn bg-[#0B2A2C] text-white 
          px-6 sm:px-8 py-3 sm:py-4 
          rounded-full 
          text-base sm:text-lg 
          font-semibold 
          transition-all duration-300 
          hover:bg-[#1a3d3f] border-2 border-[#0B2A2C] 
          shadow-lg hover:text-[#0B2A2C]"
      >
        <span>View Details →</span>
      </button>

        {/* Progress Dots */}
        <div className="service-progress flex justify-center mt-6 sm:mt-8">
  <div className="flex space-x-2 sm:space-x-3">
    {services.map((_, index) => (
      <button
        key={index}
        onClick={() => setActiveServiceIndex(index)}
        className={`
          rounded-full transition-all duration-300
          ${index === activeServiceIndex
            ? 'bg-[#0B2A2C] w-6 sm:w-8 h-2 sm:h-3'
            : 'bg-[#0B2A2C]/50 hover:bg-[#0B2A2C] w-2 h-2 sm:w-3 sm:h-3'}
        `}
        aria-label={`Select ${services[index].title}`}
      />
    ))}
  </div>
</div>

      </div>
    </div>
  </div>
</section>

  );
};

export default ServicesSection;